/**
 * Logging a dose.
 *
 * The readout is the whole screen: what to draw, and what that actually
 * delivers. The calculator shows the exact figure, the amount the barrel can
 * express, and the difference between them — see domain/reconstitution.ts for
 * why all three are shown rather than just the tidy one.
 *
 * This is a log and a calculator. It offers no dosing guidance and makes no
 * claim about what is safe or appropriate.
 */
import { useMemo, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Field, Sheet, Stepper, fmt } from '../ui/bits';
import { SYRINGES, planDose, type Syringe } from '../domain/reconstitution';
import { DEFAULT_SITES, nextSite, siteRest } from '../domain/rotation';
import { nextDueDay } from '../domain/schedule';
import { addDose, compounds, notify, protocols, recentDoses, settings, today, vials } from '../store';
import { nowIso } from '../lib/time';
import type { Compound, Vial } from '../data/schema';

/**
 * The compound to preselect: whichever is due today, else the first with a
 * schedule, else the first at all. Plain `comps[0]` is alphabetical by id,
 * which put the oral BPC-157 ahead of the weekly injectable.
 */
function defaultCompoundId(comps: Compound[]): string {
  const day = today.value;
  const due = protocols.value.find((p) => nextDueDay(p, day) === day);
  if (due) return due.compoundId;
  const scheduled = protocols.value.find((p) => p.kind !== 'asneeded');
  if (scheduled) return scheduled.compoundId;
  return comps[0]?.id ?? '';
}

export function DoseSheet(props: { onClose: () => void }): JSX.Element {
  const comps = compounds.value;
  const [compoundId, setCompoundId] = useState(() => defaultCompoundId(comps));
  const compound = comps.find((c) => c.id === compoundId);

  const protocol = protocols.value.find((p) => p.compoundId === compoundId);
  const compoundVials = vials.value.filter((v) => v.compoundId === compoundId && v.status === 'active');
  const [vialId, setVialId] = useState(compoundVials[0]?.id ?? '');
  const vial = vials.value.find((v) => v.id === vialId) ?? compoundVials[0];

  const unit = compound?.displayUnit ?? 'mg';
  const [doseDisplay, setDoseDisplay] = useState(() => {
    const mg = protocol?.doseMg ?? 1;
    return unit === 'mcg' ? mg * 1000 : mg;
  });
  const doseMg = unit === 'mcg' ? doseDisplay / 1000 : doseDisplay;

  const [syringeId, setSyringeId] = useState(settings.value.defaultSyringeId);
  const syringe: Syringe = SYRINGES[syringeId] ?? SYRINGES['u100-0.3']!;

  const sites = protocol?.sites ?? DEFAULT_SITES;
  const history = recentDoses.value
    .filter((d) => d.compoundId === compoundId && d.site)
    .map((d) => ({ site: d.site!, day: d.day }));
  const suggested = nextSite(sites, history, today.value);
  const [site, setSite] = useState<string | null>(suggested);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const injectable = compound?.route !== 'oral' && compound?.reconstituted !== false;

  const plan = useMemo(() => {
    if (!injectable || !vial || !vial.diluentMl) return null;
    return planDose({ vialMg: vial.contentMg, diluentMl: vial.diluentMl, doseMg, syringe });
  }, [injectable, vial?.contentMg, vial?.diluentMl, doseMg, syringe.id, syringe.increment]);

  const save = async () => {
    if (!compound) return;
    setBusy(true);
    try {
      await addDose({
        compoundId: compound.id,
        ...(vial ? { vialId: vial.id } : {}),
        day: today.value,
        at: nowIso(),
        // Store what was actually delivered, not what was asked for.
        doseMg: plan?.ok ? plan.deliveredMg : doseMg,
        ...(plan?.ok ? { units: plan.drawnUnits, volumeMl: plan.drawnVolumeMl, syringeId: syringe.id } : {}),
        ...(site && injectable ? { site } : {}),
        route: compound.route,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      notify(`Logged ${fmtDoseDisplay(doseDisplay, unit)}${site && injectable ? ` · ${site}` : ''}`);
      props.onClose();
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Could not save', true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      title="Log dose"
      onClose={props.onClose}
      footer={
        <button class="btn btn-primary btn-full" disabled={busy || !compound} onClick={save}>
          {busy ? 'Saving' : `Log ${fmtDoseDisplay(doseDisplay, unit)}${site && injectable ? ` · ${site}` : ''}`}
        </button>
      }
    >
      {comps.length > 1 ? (
        <div class="row wrap" style={{ gap: '6px' }}>
          {comps.map((c) => (
            <button
              key={c.id}
              class="chip"
              aria-pressed={c.id === compoundId}
              onClick={() => {
                setCompoundId(c.id);
                const p = protocols.value.find((x) => x.compoundId === c.id);
                const mg = p?.doseMg ?? 1;
                setDoseDisplay(c.displayUnit === 'mcg' ? mg * 1000 : mg);
                const v = vials.value.find((x) => x.compoundId === c.id && x.status === 'active');
                setVialId(v?.id ?? '');
              }}
            >{c.name}</button>
          ))}
        </div>
      ) : null}

      <Field label={`Dose (${unit})`}>
        <Stepper
          value={doseDisplay}
          onChange={setDoseDisplay}
          step={unit === 'mcg' ? 50 : 0.05}
          min={0}
          dp={unit === 'mcg' ? 0 : 2}
        />
      </Field>

      {injectable && plan ? <DrawReadout plan={plan} vial={vial} syringe={syringe} /> : null}

      {injectable ? (
        <>
          <Field label="Syringe">
            <select class="select" value={syringeId} onChange={(e) => setSyringeId((e.target as HTMLSelectElement).value)}>
              {Object.values(SYRINGES).map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </Field>

          <div>
            <div class="row-between" style={{ marginBottom: '8px' }}>
              <span class="label">Site</span>
              {suggested ? <span class="label">Longest rested · {suggested}</span> : null}
            </div>
            <BodyMap sites={sites} history={history} selected={site} suggested={suggested} onSelect={setSite} />
          </div>
        </>
      ) : null}

      <Field label="Note">
        <input class="input" style={{ fontFamily: 'var(--sans)' }} value={note} placeholder="Optional"
          onInput={(e) => setNote((e.target as HTMLInputElement).value)} />
      </Field>

      {injectable && (!vial || !vial.diluentMl) ? (
        <p class="note note-warn">
          No reconstituted vial for this compound, so units cannot be calculated. Add one under Peptides.
        </p>
      ) : null}
    </Sheet>
  );
}

/** The instrument readout: draw this much, and this is what it delivers. */
function DrawReadout(props: {
  plan: ReturnType<typeof planDose>;
  vial: Vial | undefined;
  syringe: Syringe;
}): JSX.Element {
  const { plan, vial, syringe } = props;
  if (!plan.ok) {
    return (
      <div class="card">
        {plan.issues.map((i) => <p key={i} class="note">{i}</p>)}
      </div>
    );
  }

  const pct = Math.min(100, (plan.drawnUnits / syringe.capacityUnits) * 100);

  return (
    <div class="card">
      <div class="row-between" style={{ alignItems: 'flex-end' }}>
        <div class="col">
          <span class="label">Draw to</span>
          <span class="row" style={{ gap: '6px', alignItems: 'baseline' }}>
            <span class="readout readout-xl" style={{ color: 'var(--coral)' }}>{fmt(plan.drawnUnits, 1)}</span>
            <span class="unit" style={{ fontSize: '14px' }}>units</span>
          </span>
        </div>
        <div class="col" style={{ alignItems: 'flex-end' }}>
          <span class="label">Delivers</span>
          <span class="readout readout-md">{fmt(plan.deliveredMg, 3)}<span class="unit"> mg</span></span>
        </div>
      </div>

      {/* The barrel, drawn to the same graduated language as every other meter. */}
      <div class="meter" style={{ marginTop: '12px' }}>
        <div class="meter-track" style={{ height: '30px' }}>
          <div class="meter-fill" style={{ width: `${pct}%` }} />
          <div class="meter-value">
            <span class="dim">0</span>
            <span class="dim">{syringe.capacityUnits} u</span>
          </div>
        </div>
      </div>

      <div class="divider" />

      <div class="row wrap" style={{ gap: '14px' }}>
        <span class="unit">
          {vial ? `${fmt(vial.contentMg, 2)} mg / ${fmt(vial.diluentMl ?? 0, 2)} mL` : ''}
        </span>
        <span class="unit">= {fmt(plan.concentrationMgPerMl, 3)} mg/mL</span>
        <span class="unit">{fmt(plan.drawnVolumeMl, 3)} mL</span>
      </div>

      {!plan.exactOnMark ? (
        <p class="note note-warn">
          Exact dose is {fmt(plan.exactUnits, 2)} u, between marks on this barrel.
          Drawing {fmt(plan.drawnUnits, 1)} u delivers {plan.deltaMg > 0 ? '+' : ''}{fmt(plan.deltaMg, 3)} mg
          ({plan.deltaPct > 0 ? '+' : ''}{fmt(plan.deltaPct, 1)}%).
        </p>
      ) : null}

      {plan.exceedsCapacity ? (
        <p class="note note-bad">
          That is {fmt(plan.exactUnits, 1)} u — more than this {syringe.capacityUnits} u barrel holds.
        </p>
      ) : null}
    </div>
  );
}

/**
 * Injection sites as a schematic body.
 * Abstract on purpose: it has to read at a glance at arm's length, and a
 * realistic figure would carry detail that means nothing here.
 */
function BodyMap(props: {
  sites: string[];
  history: { site: string; day: string }[];
  selected: string | null;
  suggested: string | null;
  onSelect: (site: string) => void;
}): JSX.Element {
  const rest = siteRest(props.sites, props.history, today.value);
  const restOf = (site: string) => rest.find((r) => r.site === site)?.restDays ?? null;

  // x, y, w, h per zone in the 100x140 viewBox.
  const ZONES: Record<string, [number, number, number, number]> = {
    'arm-L': [8, 40, 13, 26],
    'arm-R': [79, 40, 13, 26],
    'abdomen-L': [32, 52, 16, 20],
    'abdomen-R': [52, 52, 16, 20],
    'glute-L': [32, 76, 16, 14],
    'glute-R': [52, 76, 16, 14],
    'thigh-L': [32, 94, 16, 30],
    'thigh-R': [52, 94, 16, 30],
  };

  return (
    <>
      <svg class="bodymap" viewBox="0 0 100 140" role="group" aria-label="Injection sites">
        {/* The body, as quiet scaffolding. */}
        <g fill="none" stroke="var(--line)" stroke-width="1">
          <circle cx="50" cy="16" r="9" />
          <rect x="30" y="28" width="40" height="46" rx="6" />
          <rect x="30" y="92" width="16" height="34" rx="5" />
          <rect x="54" y="92" width="16" height="34" rx="5" />
          <rect x="16" y="32" width="10" height="36" rx="5" />
          <rect x="74" y="32" width="10" height="36" rx="5" />
        </g>
        {props.sites.map((site) => {
          const box = ZONES[site];
          if (!box) return null;
          const [x, y, w, h] = box;
          const days = restOf(site);
          const recent = days !== null && days <= 3;
          const classes = [
            'zone',
            props.selected === site ? 'is-selected' : '',
            recent && props.selected !== site ? 'is-recent' : '',
            props.suggested === site && props.selected !== site ? 'is-next' : '',
          ].filter(Boolean).join(' ');
          return (
            <g key={site} onClick={() => props.onSelect(site)} style={{ cursor: 'pointer' }}>
              <rect class={classes} x={x} y={y} width={w} height={h} rx="3" />
              <title>{site}{days !== null ? ` — rested ${days}d` : ' — never used'}</title>
            </g>
          );
        })}
      </svg>
      <div class="row wrap" style={{ gap: '6px', marginTop: '10px' }}>
        {props.sites.map((s) => {
          const days = restOf(s);
          return (
            <button key={s} class="chip" aria-pressed={props.selected === s} onClick={() => props.onSelect(s)}>
              {s}
              <span class="dim">{days === null ? 'new' : `${days}d`}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}

function fmtDoseDisplay(value: number, unit: 'mg' | 'mcg'): string {
  return `${fmt(value, unit === 'mcg' ? 0 : 2)} ${unit}`;
}

